import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { InsegnamentoItem, InsegnamentoService } from '../common/services/insegnamento.service';
import { FeatureFlagGuard } from './feature-flag-guard';

export const TeachingRedirectGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
  const insegnamentoService = inject(InsegnamentoService);
  const router = inject(Router);
  const featureFlagGuard = inject(FeatureFlagGuard);

  const routesToCheck = [
    { path: 'overview', featureName: 'overview' },
    { path: 'documents', featureName: 'document' }
  ];

  const createRouteSnapshot = (featureName: string): ActivatedRouteSnapshot => {
      return {
          data: { featureName: featureName },
          params: {},
          queryParams: {},
          fragment: '',
          url: [],
      } as unknown as ActivatedRouteSnapshot;
  };

  return insegnamentoService.isLoaded$.pipe(
    filter((loaded: boolean) => loaded),
    take(1),
    switchMap(() =>
      combineLatest([
        insegnamentoService.loadError$,
        insegnamentoService.teachings$
      ]).pipe(take(1))
    ),
    switchMap(([hasError, insegnamenti]: [boolean, InsegnamentoItem[]]) => {
      if (hasError) {
        return of(router.createUrlTree(['/error-page']));
      }

      if (!insegnamenti || insegnamenti.length !== 1) {
        return of(true);
      }

      const singleInsegnamento = insegnamenti[0];
      insegnamentoService.updateSelection(singleInsegnamento.id);

      const overviewRoute = routesToCheck[0];
      const documentsRoute = routesToCheck[1];

      const overviewSnapshot = createRouteSnapshot(overviewRoute.featureName);

      return featureFlagGuard.canActivate(overviewSnapshot).pipe(
        switchMap((canActivateOverview: boolean | UrlTree) => {
          if (canActivateOverview === true) {
            router.navigate([`/${overviewRoute.path}`]);
            return of(false);
          } else {
            const documentsSnapshot = createRouteSnapshot(documentsRoute.featureName);

            return featureFlagGuard.canActivate(documentsSnapshot).pipe(
              map((canActivateDocuments: boolean | UrlTree) => {
                if (canActivateDocuments === true) {
                  router.navigate([`/${documentsRoute.path}`]);
                  return false;
                } else {
                  return true;
                }
              })
            );
          }
        })
      );
    })
  );
};